// Ubicación en el repo: src/lib/exams.js
// Audiometrías (spec-prototipo.md 1.3, exams). Fase 1 del módulo (ver
// DECISIONS.md): CRUD básico — paciente, fecha, profesional, equipo,
// notas. Los campos de vía aérea/ósea/LDL/diapasones/logoaudiometría se
// suman en fases siguientes, ya sobre este mismo archivo.
import { supabase } from './supabaseClient';

// Mismas listas de frecuencias que el prototipo (línea ~1679 y ~1755).
export const FREQS = [125, 250, 500, 1000, 2000, 3000, 4000, 6000, 8000];
export const BONE_FREQS = [250, 500, 1000, 2000, 3000, 4000];
export const LDL_FREQS = [250, 500, 1000, 2000, 3000, 4000];
export const PTA_FREQS = [500, 1000, 2000, 4000]; // promedio tonal puro (línea ~1680)

// Promedio tonal puro (PTA/PTP): promedio de las frecuencias del modo
// elegido. No se aproxima a 1 decimal (antes sí, ej. 33.75 → 33.8): al
// dividir por 3 o 4 el resultado exacto nunca tiene más de 2 decimales, así
// que solo se redondea a 2 decimales por las dudas (evita basura de punto
// flotante tipo 20.099999999999998), sin perder precisión real.
//
// `mode` (migración 0057, 15-oct-2026, pedido de Nicolás): el promedio es
// seleccionable por examen — 4 frecuencias (500/1000/2000/4000, el de
// siempre y el default) o 3 frecuencias (500/1000/2000).
//
// REGLA (confirmada por Nicolás, 15-oct-2026): se exigen TODAS las
// frecuencias del modo elegido, cada una con umbral. Si alguna está vacía o
// marcada "SR" (sin respuesta, `nr`, mapa { frecuencia: true } del
// oído/vía correspondiente, opcional), el promedio es NO CALCULABLE y se
// devuelve null (la UI lo muestra como "No calculable"). No se calcula un
// promedio parcial con las frecuencias restantes.
export const PTA_FREQS_3 = [500, 1000, 2000];
export function ptaFreqsFor(mode) {
  return Number(mode) === 3 ? PTA_FREQS_3 : PTA_FREQS;
}
export function ptaLabel(mode) {
  return ptaFreqsFor(mode).join('-');
}
export function calcPTA(data, mode = 4, nr) {
  if (!data) return null;
  const freqs = ptaFreqsFor(mode);
  const hasThreshold = (f) => {
    const v = data[f];
    return v !== undefined && v !== null && v !== '' && !nr?.[f];
  };
  if (!freqs.every(hasThreshold)) return null;
  const sum = freqs.reduce((a, f) => a + Number(data[f]), 0);
  return Math.round((sum / freqs.length) * 100) / 100;
}

// jsonb de la base guarda { "1000": 20, ... } con las frecuencias sin
// valor cargado simplemente ausentes del objeto (no null, no ''). Estos
// dos helpers convierten entre eso y el estado de los inputs del form.
export function freqValuesToInputs(freqObj) {
  const out = {};
  for (const [k, v] of Object.entries(freqObj || {})) {
    if (v !== null && v !== undefined) out[k] = String(v);
  }
  return out;
}
export function freqInputsToValues(inputsObj) {
  const out = {};
  for (const [k, v] of Object.entries(inputsObj || {})) {
    if (v !== '' && v !== null && v !== undefined) out[k] = Number(v);
  }
  return out;
}

// Límite de umbral (migración 0053, 16-sep-2026, pedido de Nicolás): 0 a
// 120 dB, de 5 en 5. Se valida acá (además de min/max/step en el input, que
// solo cubre las flechitas del navegador, no un valor tipeado a mano o
// pegado) antes de guardar — mismo criterio del proyecto de nunca confiar
// solo en el navegador. Devuelve la lista de "frecuencia: valor" fuera de
// rango, vacía si todo está bien.
export function invalidFreqEntries(inputsObj) {
  const bad = [];
  for (const [k, v] of Object.entries(inputsObj || {})) {
    if (v === '' || v === null || v === undefined) continue;
    const n = Number(v);
    if (Number.isNaN(n) || n < 0 || n > 120 || n % 5 !== 0) bad.push(`${k} Hz: ${v} dB`);
  }
  return bad;
}

// Enmascaramiento por frecuencia (migración 0053, 16-sep-2026, pedido de
// Nicolás): mapas booleanos separados ({ "1000": true, ... }, solo las
// frecuencias marcadas presentes) — a propósito NO se mezclan con
// od/oi/bone_od/bone_oi (que siguen siendo solo el valor en dB) para no
// tocar el cálculo del PTP ni la comparación/tendencia de exámenes, que ya
// leen esas columnas tal cual. Mismo par de helpers que freqValues/
// freqInputs, pero para booleanos en vez de números.
export function freqMaskValuesToInputs(maskObj) {
  const out = {};
  for (const [k, v] of Object.entries(maskObj || {})) {
    if (v) out[k] = true;
  }
  return out;
}
export function freqMaskInputsToValues(maskObj) {
  const out = {};
  for (const [k, v] of Object.entries(maskObj || {})) {
    if (v) out[k] = true;
  }
  return out;
}

// `patient_branches(branch_id, branches(logo_path))` trae de una vez el
// logo de la sucursal del paciente — se usa al imprimir el informe
// (DECISIONS.md #28 y #39) sin necesitar una consulta aparte.
// `email` sumado acá (22-sep-2026, DECISIONS.md #119): "Enviar por correo"
// en el detalle de audiometría necesita saber si el paciente tiene correo
// cargado para habilitar/deshabilitar ese botón, sin pedirlo aparte.
const EXAM_LIST_SELECT =
  'id, patient_id, date, equipment, notes, professional_id, ' +
  'patients(nombre, apellido, segundo_nombre, segundo_apellido, rut, birthdate, email, patient_branches(branch_id, branches(logo_path))), ' +
  'professionals!professional_id(nombre, apellido, role, rut, signature_path)';

// Trae todas las audiometrías a las que el profesional tiene acceso por
// RLS (patient_branch_match), y recién ahí filtra por la sucursal activa
// elegida en el header — mismo patrón en dos capas que el resto de la app
// (RLS = seguridad real, este filtro = solo UX). No hay una vista tipo
// patient_list_stats para exams, así que se filtra en el cliente sobre el
// embed de patient_branches en vez de un .contains() de servidor.
export async function listExams({ branchId, search } = {}) {
  const { data, error } = await supabase
    .from('exams')
    .select(EXAM_LIST_SELECT)
    .is('deleted_at', null)
    .order('date', { ascending: false });
  if (error) throw error;

  let rows = data ?? [];
  if (branchId) {
    rows = rows.filter((e) =>
      (e.patients?.patient_branches ?? []).some((pb) => pb.branch_id === branchId)
    );
  }
  const term = search?.trim().toLowerCase();
  if (term) {
    rows = rows.filter((e) => {
      const p = e.patients;
      if (!p) return false;
      const name = [p.nombre, p.segundo_nombre, p.apellido, p.segundo_apellido]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return name.includes(term) || (p.rut || '').toLowerCase().includes(term);
    });
  }
  return rows;
}

export async function getExamById(examId) {
  const { data, error } = await supabase.from('exams').select('*').eq('id', examId).single();
  if (error) throw error;
  return data;
}

export async function createExam(values, actorId) {
  const { error } = await supabase.from('exams').insert({
    ...values,
    created_by: actorId,
    updated_by: actorId,
  });
  if (error) throw error;
}

export async function updateExam(examId, values, actorId) {
  const { error } = await supabase
    .from('exams')
    .update({ ...values, updated_by: actorId })
    .eq('id', examId);
  if (error) throw error;
}

export async function softDeleteExam(examId, actorId) {
  const { error } = await supabase
    .from('exams')
    .update({ deleted_at: new Date().toISOString(), deleted_by: actorId })
    .eq('id', examId);
  if (error) throw error;
}
